import * as React from "react";
import { userHasPermission } from "@eduviaos/shared";
import { useAuth } from "@/auth/AuthProvider";

/**
 * Phase F-finale-5 Slice 5a — single source of truth for the IA-side
 * forms-authoring write-gate.
 *
 * Mirrors `useCanAuthorAssessments` (Phase H5d). Combines two independent
 * signals:
 *
 *   1. **RBAC** — the principal must hold `manage_forms` (base role catalog
 *      or designation override). `admin` carries it by default; `teacher`
 *      and other staff roles do not.
 *   2. **Tenant capability** — the SA must not have disabled
 *      `forms_editable_by_tenant`. Default `True` (concierge OFF) — legacy
 *      and freshly minted tenants are editable out of the box.
 *
 * Returns three orthogonal booleans so consumers render the right surface
 * for each rejection reason:
 *
 *   - `canEditForms` — overall write-eligibility. Hide / disable the Forms
 *     nav entry + all editor write controls when `false`.
 *   - `missingRbac` — logged in but lacks `manage_forms` ("ask your admin").
 *   - `pausedByTenant` — has the RBAC but the SA paused forms editing for the
 *     tenant (concierge mode). Surface the paused banner.
 *
 * `pausedByTenant` only fires when `missingRbac === false`, so the two
 * banners never stack.
 */
export interface FormsAuthoringGate {
  canEditForms: boolean;
  missingRbac: boolean;
  pausedByTenant: boolean;
}

export function useCanEditForms(): FormsAuthoringGate {
  const { user, featureFlags } = useAuth();
  return React.useMemo<FormsAuthoringGate>(() => {
    if (!user) {
      return { canEditForms: false, missingRbac: false, pausedByTenant: false };
    }
    const hasRbac = userHasPermission(
      user.role,
      user.permission_overrides ?? [],
      "manage_forms",
      user.permission_mode ?? "additive",
    );
    // `null` (super_admin synthetic tenant / pre-/auth/me) and `undefined`
    // mean "no gating" — matches the BE contract.
    const flagEnabled = featureFlags?.forms_editable_by_tenant !== false;
    return {
      canEditForms: hasRbac && flagEnabled,
      missingRbac: !hasRbac,
      pausedByTenant: hasRbac && !flagEnabled,
    };
  }, [user, featureFlags]);
}
